import type {
    UseFormReturn,
    SubmitHandler,
    UseFormProps,
    Path,
    DeepPartial,
    SubmitErrorHandler,
    FieldValues,
    DefaultValues,
} from 'react-hook-form';
import {FormProvider, useForm} from 'react-hook-form';
import type {AnyObjectSchema, ObjectSchema} from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useEffect} from 'react';
type ServerErrors<T> = {
    [Property in keyof T]: string;
};
type FormProps<TFormValues extends FieldValues> = {
    onSubmit: SubmitHandler<TFormValues>;
    onError?: SubmitErrorHandler<FieldValues>;
    children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
    useFormProps?: UseFormProps<TFormValues>;
    validationSchema?: AnyObjectSchema;
    serverError?: ServerErrors<Partial<TFormValues>> | null;
    resetValues?: DefaultValues<TFormValues>;
    className?: string;
    [key: string]: unknown;
};

export const Form = <TFormValues extends Record<string, any> = Record<string, any>>({
    onSubmit,
    onError,
    children,
    useFormProps,
    validationSchema,
    serverError,
    resetValues,
    ...props
}: FormProps<TFormValues>) => {
    const formOptions: UseFormProps<TFormValues> = {
        ...useFormProps,
    };

    if (validationSchema) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formOptions.resolver = yupResolver(validationSchema as any) as any;
    }

    const methods = useForm<TFormValues>(formOptions);

    useEffect(() => {
        if (serverError) {
            Object.entries(serverError).forEach(([key, value]) => {
                methods.setError(key as Path<TFormValues>, {
                    type: 'manual',
                    message: value,
                });
            });
        }
    }, [serverError, methods]);

    useEffect(() => {
        if (resetValues) {
            methods.reset(resetValues);
        }
    }, [resetValues, methods]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)} noValidate {...props}>
                {children(methods)}
            </form>
        </FormProvider>
    );
};
